/**
 * Canonical English metric titles for widget info (matches API welcome_metric_catalog).
 * Keep in sync with Welcome/index.js WELCOME_METRIC_DEFAULT_EN.
 */
export const WELCOME_METRIC_CATALOG_EN = {
  1: "Most Increasing KPI",
  2: "Most Decreasing KPI",
  3: "Subscription Details",
  4: "Latest Reports",
  5: "Latest Exports",
  6: "Alarms Hit",
  7: "Insight Notes Created",
  8: "Review Statistics",
  9: "Average Rating",
  10: "Positive Sentiment Score",
  29: "Praise Intent Score",
  11: "Sentiment Distribution",
  12: "Intent Distribution",
  13: "Rating per Topic",
  14: "Sentiment per Topic",
  15: "Participation per Topic",
  16: "Emergent Topics",
  17: "Intent per Topic",
  18: "Digital Experience Score",
  19: "AI Insights (Root Cause)",
  20: "Root Cause & Recommendations",
  21: "General Summary",
  22: "Topic-wise Summary",
  23: "Key Drivers Summary",
  24: "Topic-wise Key Drivers Summary",
  25: "Highlights Summary",
  26: "Topic-wise Highlights Summary",
  27: "Decision Analysis",
  28: "Hot Terms",
};

const GENAI_INFO_METRIC_IDS = new Set([20, 21, 22, 23, 24, 25, 26, 27, 28]);

function welcomeMetricLabelForMetricId(metricId, t) {
  const mid = Number(metricId);
  if (!Number.isFinite(mid) || mid <= 0) return null;
  const infoKey = `welcome.metricInfoType_${mid}`;
  const infoTr = t(infoKey);
  if (infoTr !== infoKey && String(infoTr).trim() !== "") return infoTr;
  const nameKey = `welcome.metricName_${mid}`;
  const nameTr = t(nameKey);
  if (nameTr !== nameKey && String(nameTr).trim() !== "") return nameTr;
  return WELCOME_METRIC_CATALOG_EN[mid] ?? null;
}

function parseMetricIdFromMetricNLabel(text) {
  const m = /^Metric\s+(\d+)$/i.exec(String(text ?? "").trim());
  if (!m) return null;
  const parsed = Number(m[1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function welcomeMetricNameLooksStaleForId(rawName, metricId) {
  const raw = String(rawName ?? "").trim();
  if (!raw) return false;
  const mid = Number(metricId);
  if (!Number.isFinite(mid) || mid <= 0) return false;
  const low = raw.toLowerCase();
  const catalogNames = new Set(Object.values(WELCOME_METRIC_CATALOG_EN));
  for (const [otherIdStr, otherEn] of Object.entries(WELCOME_METRIC_CATALOG_EN)) {
    if (Number(otherIdStr) !== mid && raw === otherEn) return true;
  }
  if (catalogNames.has(raw)) {
    const canonical = WELCOME_METRIC_CATALOG_EN[mid];
    return Boolean(canonical && raw !== canonical);
  }
  if (mid === 25 && (low.includes("key driver") || low === "general summary")) {
    return true;
  }
  if (mid === 23 && low.includes("highlight")) return true;
  if (mid === 21 && (low.includes("key driver") || low.includes("highlight"))) {
    return true;
  }
  if (/^Metric\s+\d+$/i.test(raw)) return true;
  return false;
}

function resolveWidgetInfoMetricId(widgetInfo, widgetRow) {
  const direct = Number(widgetInfo?.metric_id ?? widgetRow?.metric_id);
  if (Number.isFinite(direct) && direct > 0) {
    return direct;
  }
  const apiType = String(widgetInfo?.widget_type_name ?? "").trim();
  const fromType = /^Metric\s+(\d+)$/i.exec(apiType);
  if (fromType) {
    const parsed = Number(fromType[1]);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  const rawName = String(widgetInfo?.name ?? widgetRow?.name ?? "").trim();
  if (rawName) {
    const low = rawName.toLowerCase();
    for (const [idStr, label] of Object.entries(WELCOME_METRIC_CATALOG_EN)) {
      if (rawName === label || low === String(label).toLowerCase()) {
        return Number(idStr);
      }
    }
  }
  return null;
}

function resolveWidgetInfoReviewCountFromKpi(widgetKpiData, metricId) {
  const data = widgetKpiData?.data;
  if (!data || typeof data !== "object") return null;
  for (const key of ["source_review_count", "review_count", "num_of_reviews"]) {
    const raw = data[key];
    if (raw != null && raw !== "" && Number.isFinite(Number(raw))) {
      return Number(raw);
    }
  }
  const mid = Number(metricId);
  if (mid === 27 && Array.isArray(data.summary_tabs)) {
    const general = data.summary_tabs.find(
      (tab) => tab && String(tab.id || "").toLowerCase() === "general",
    );
    const raw = general?.review_count ?? general?.reviewCount;
    if (raw != null && raw !== "" && Number.isFinite(Number(raw))) {
      return Number(raw);
    }
  }
  return null;
}

export function resolveWidgetInfoReviewCount(apiSuccess, widgetRow, enrich = {}) {
  const direct = apiSuccess?.review_count_total ?? widgetRow?.review_count_total;
  if (direct != null && direct !== "" && Number.isFinite(Number(direct))) {
    return Number(direct);
  }
  const fromKpi = resolveWidgetInfoReviewCountFromKpi(
    enrich.widgetKpiData,
    resolveWidgetInfoMetricId(apiSuccess, widgetRow),
  );
  if (fromKpi != null) return fromKpi;
  const reportRc = enrich.report?.review_count;
  if (reportRc != null && reportRc !== "" && Number.isFinite(Number(reportRc))) {
    return Number(reportRc);
  }
  return null;
}

/** Merge API info with the widget row from widgets/load (metric_id is required for labels). */
export function mergeWidgetInfoPayload(apiSuccess, widgetRow, enrich = {}) {
  if (!apiSuccess || typeof apiSuccess !== "object") {
    return apiSuccess;
  }
  const payload = { ...apiSuccess };
  const mid = resolveWidgetInfoMetricId(payload, widgetRow);
  if (mid != null) {
    payload.metric_id = mid;
  }
  if (widgetRow?.id != null) {
    payload.widget_id = Number(widgetRow.id);
  }
  const reviewCount = resolveWidgetInfoReviewCount(payload, widgetRow, enrich);
  if (reviewCount != null) {
    payload.review_count_total = reviewCount;
  }
  const kpiData = enrich.widgetKpiData?.data;
  if (kpiData && typeof kpiData === "object") {
    if (kpiData.scope_review_count_total != null) {
      payload.scope_review_count_total = kpiData.scope_review_count_total;
    }
    if (kpiData.review_analysis_capped != null) {
      payload.review_analysis_capped = kpiData.review_analysis_capped;
    }
    if (kpiData.review_analysis_limit != null) {
      payload.review_analysis_limit = kpiData.review_analysis_limit;
    }
    if (kpiData.source_review_count != null && payload.source_review_count == null) {
      payload.source_review_count = kpiData.source_review_count;
    }
  }
  if (!payload.welcome_group_name && enrich.report?.welcome_group_name) {
    payload.welcome_group_name = enrich.report.welcome_group_name;
  }
  if (
    (payload.pivot_filter == null ||
      (typeof payload.pivot_filter === "object" &&
        Object.keys(payload.pivot_filter).length === 0)) &&
    widgetRow?.pivot_filter != null
  ) {
    payload.pivot_filter = widgetRow.pivot_filter;
  }
  return payload;
}

/** Widget type row in info modal — from metric_id + i18n (never bare "Metric N"). */
export function resolveWidgetInfoMetricTypeName(widgetInfo, t, widgetRow) {
  const apiLabel = String(widgetInfo?.widget_type_name ?? "").trim();
  let mid = resolveWidgetInfoMetricId(widgetInfo, widgetRow);
  if (mid == null) {
    mid = parseMetricIdFromMetricNLabel(apiLabel);
  }
  if (mid != null) {
    const label = welcomeMetricLabelForMetricId(mid, t);
    if (label) return label;
  }
  if (apiLabel && !/^Metric\s+\d+$/i.test(apiLabel)) {
    return apiLabel;
  }
  return "—";
}

/** Name row in info modal — correct stale GenAI catalog snapshots. */
export function resolveWidgetInfoDisplayName(widgetInfo, t, widgetRow) {
  const mid = resolveWidgetInfoMetricId(widgetInfo, widgetRow);
  const raw = String(widgetInfo?.name ?? "").trim();
  if (mid == null) {
    return raw || "—";
  }
  const canonical =
    welcomeMetricLabelForMetricId(mid, t) ?? WELCOME_METRIC_CATALOG_EN[mid] ?? null;
  if (!raw) {
    return canonical || "—";
  }
  if (GENAI_INFO_METRIC_IDS.has(mid) && welcomeMetricNameLooksStaleForId(raw, mid)) {
    return canonical || raw;
  }
  if (GENAI_INFO_METRIC_IDS.has(mid) && canonical && raw === canonical) {
    return canonical;
  }
  if (welcomeMetricNameLooksStaleForId(raw, mid)) {
    return canonical || raw;
  }
  return raw;
}

/** Same stale-name rules as info modal (widget card header base title). */
export function resolveWelcomeWidgetBaseDisplayName(widget, t) {
  const mid = widget?.metric_id;
  if (mid == null) {
    return rawOrEmpty(widget?.name);
  }
  const defaultEn = WELCOME_METRIC_CATALOG_EN[Number(mid)];
  const name = String(widget?.name ?? "").trim();

  if (welcomeMetricNameLooksStaleForId(name, mid)) {
    const trKey = `welcome.metricName_${mid}`;
    const translated = t(trKey);
    if (translated !== trKey) return translated;
    return defaultEn ?? name;
  }

  const trKey = `welcome.metricName_${mid}`;
  const translated = t(trKey);
  if (defaultEn && name === defaultEn) {
    return translated !== trKey ? translated : name;
  }
  if (!name || /^Metric\s+\d+$/i.test(name)) {
    return translated !== trKey ? translated : defaultEn ?? name;
  }
  return name;
}

function rawOrEmpty(v) {
  const s = String(v ?? "").trim();
  return s || "—";
}
